import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class SessionStorageService {
  private hasStorage = true;

  constructor() {
    // Check if sessionStorage is available
    try {
      sessionStorage.setItem('test', 'test');
      sessionStorage.removeItem('test');
      this.hasStorage = true;
    } catch (e) {
      this.hasStorage = false;
      console.warn('SessionStorage is not available, state will not persist');
    }
  }

  /**
   * Gets a boolean value from session storage
   * @param key The storage key
   * @param defaultValue Optional default value if key doesn't exist (defaults to false)
   * @returns boolean value from storage, or defaultValue if not found
   */
  getBoolean(key: string, defaultValue = false): boolean {
    if (!this.hasStorage) return defaultValue;

    try {
      const value = sessionStorage.getItem(key);
      return value === 'true';
    } catch (e) {
      return defaultValue;
    }
  }

  /**
   * Sets a boolean value in session storage
   * @param key The storage key
   * @param value The boolean value to store
   * @returns true if operation was successful, false otherwise
   */
  setBoolean(key: string, value: boolean): boolean {
    if (!this.hasStorage) return false;

    try {
      if (value) {
        sessionStorage.setItem(key, 'true');
      } else {
        sessionStorage.removeItem(key);
      }
      return true;
    } catch (e) {
      console.warn('Failed to save to session storage:', key);
      return false;
    }
  }

  /**
   * Gets a string value from session storage
   * @param key The storage key
   * @param defaultValue Optional default value if key doesn't exist
   * @returns string value from storage, or defaultValue if not found
   */
  getString(key: string, defaultValue = ''): string {
    if (!this.hasStorage) return defaultValue;

    try {
      const value = sessionStorage.getItem(key);
      return value ?? defaultValue;
    } catch (e) {
      return defaultValue;
    }
  }

  /**
   * Sets a string value in session storage
   * @param key The storage key
   * @param value The string value to store
   * @returns true if operation was successful, false otherwise
   */
  setString(key: string, value: string): boolean {
    if (!this.hasStorage) return false;

    try {
      sessionStorage.setItem(key, value);
      return true;
    } catch (e) {
      console.warn('Failed to save to session storage:', key);
      return false;
    }
  }

  /**
   * Removes an item from session storage
   * @param key The storage key to remove
   * @returns true if operation was successful, false otherwise
   */
  remove(key: string): boolean {
    if (!this.hasStorage) return false;

    try {
      sessionStorage.removeItem(key);
      return true;
    } catch (e) {
      console.warn('Failed to remove from session storage:', key);
      return false;
    }
  }

  /**
   * Checks if session storage is available
   * @returns true if session storage is available, false otherwise
   */
  isAvailable(): boolean {
    return this.hasStorage;
  }
}
